import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Building2,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  Receipt,
  Users,
  CheckCircle,
  Clock,
  FileText,
  TrendingUp
} from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import { useAdminBilling, EstablishmentBillingData } from "@/hooks/useAdminBilling";
import { formatCHF, getPlanLabel, isValidPlan, BILLING_PLANS } from "@/lib/billingPlans";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SendPaymentLinkDialog } from "@/components/admin/SendPaymentLinkDialog";

const MONTH_LABELS: Record<string, string> = {
  '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
  '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
  '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
};

function getMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-');
  return `${MONTH_LABELS[monthNum]} ${year}`;
}

function generateMonthOptions(): { value: string; label: string }[] {
  const options = [];
  const now = new Date();
  
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    options.push({ value, label: getMonthLabel(value) });
  }
  
  return options;
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'paid':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle className="w-3 h-3 mr-1" />
          Payée
        </Badge>
      );
    case 'invoiced':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
          <FileText className="w-3 h-3 mr-1" />
          Facturée
        </Badge>
      );
    default:
      return (
        <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
          <Clock className="w-3 h-3 mr-1" />
          En attente
        </Badge>
      );
  }
}

interface EstablishmentCardProps {
  item: EstablishmentBillingData;
  onUpdateStatus: (status: 'pending' | 'invoiced' | 'paid') => void;
  onUpdateNotes: (notes: string) => void;
  onUpdateInvoiceNumber: (number: string) => void;
  onUpdatePlan: (planType: string) => void;
}

function EstablishmentCard({ item, onUpdateStatus, onUpdateNotes, onUpdateInvoiceNumber, onUpdatePlan }: EstablishmentCardProps) {
  const { establishment, stats, billing, record } = item;
  const [localNotes, setLocalNotes] = useState(record?.notes || '');
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState(record?.invoice_number || '');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const hasInvalidPlan = !isValidPlan(establishment.planType);

  return (
    <Card className={cn(!establishment.isActive && 'opacity-60', hasInvalidPlan && 'border-amber-500')}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              {establishment.name}
              {!establishment.isActive && (
                <Badge variant="secondary" className="text-xs">Inactif</Badge>
              )}
              {hasInvalidPlan && (
                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs">
                  ⚠️ Plan inconnu
                </Badge>
              )}
            </CardTitle>
            <div className="mt-2">
              <Select value={establishment.planType} onValueChange={onUpdatePlan}>
                <SelectTrigger className="w-[220px] h-8 text-sm">
                  <SelectValue placeholder="Sélectionner un plan" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BILLING_PLANS).map(([key, plan]) => (
                    <SelectItem key={key} value={key}>
                      {getPlanLabel(key)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <StatusBadge status={record?.status || 'pending'} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-primary" />
            <span className="font-semibold">{stats.arrivedCount}</span>
            <span className="text-muted-foreground">réservations venues</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>{stats.arrivedPeople}</span>
            <span className="text-muted-foreground">personnes</span>
          </div>
        </div>

        {/* Calcul de facturation */}
        {!billing.isFree && (
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span>Abonnement base</span>
              <span>{formatCHF(billing.baseAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span>+ {billing.reservationCount} réservations</span>
              <span>{formatCHF(billing.reservationAmount)}</span>
            </div>
            <div className="border-t border-border my-2" />
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span>{formatCHF(billing.subtotal)}</span>
            </div>
            {billing.maxMonthly > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Plafond mensuel</span>
                <span>{formatCHF(billing.maxMonthly)}</span>
              </div>
            )}
            <div className="border-t border-border my-2" />
            <div className="flex justify-between font-bold text-base">
              <span>Total HT</span>
              <span className={billing.isCapped ? 'text-amber-600' : 'text-primary'}>
                {formatCHF(billing.total)}
                {billing.isCapped && ' (plafonné)'}
              </span>
            </div>
          </div>
        )}

        {billing.isFree && (
          <div className="bg-muted/50 rounded-lg p-3 text-sm text-center text-muted-foreground">
            {billing.planName === 'Essai' ? 'Période d\'essai - Gratuit' : 'Plan gratuit'}
          </div>
        )}

        {/* Actions de facturation */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium w-20">Statut</label>
            <Select 
              value={record?.status || 'pending'} 
              onValueChange={(value) => onUpdateStatus(value as 'pending' | 'invoiced' | 'paid')}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="invoiced">Facturée</SelectItem>
                <SelectItem value="paid">Payée</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium w-20">N° facture</label>
            <Input
              placeholder="Ex: FAC-2026-001"
              value={localInvoiceNumber}
              onChange={(e) => setLocalInvoiceNumber(e.target.value)}
              onBlur={() => onUpdateInvoiceNumber(localInvoiceNumber)}
              className="flex-1"
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1">Notes</label>
            <Textarea
              placeholder="Notes internes..."
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              onBlur={() => onUpdateNotes(localNotes)}
              className="min-h-[60px]"
            />
          </div>

          {/* Dates */}
          {record?.invoice_sent_at && (
            <p className="text-xs text-muted-foreground">
              Facture envoyée le {new Date(record.invoice_sent_at).toLocaleDateString('fr-CH')}
            </p>
          )}
          {record?.paid_at && (
            <p className="text-xs text-green-600">
              Payée le {new Date(record.paid_at).toLocaleDateString('fr-CH')}
            </p>
          )}

          {/* Stripe payment link */}
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2 border-blue-500/30 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
            onClick={() => setPaymentDialogOpen(true)}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Envoyer lien de paiement Stripe
          </Button>
        </div>
      </CardContent>

      <SendPaymentLinkDialog
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        establishmentId={establishment.id}
        establishmentName={establishment.name}
      />
    </Card>
  );
}

export default function AdminBilling() {
  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.value || '');
  
  const { data, summary, loading, error, updateBillingRecord } = useAdminBilling(selectedMonth);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const currentIndex = monthOptions.findIndex(m => m.value === selectedMonth);
    if (direction === 'prev' && currentIndex < monthOptions.length - 1) {
      setSelectedMonth(monthOptions[currentIndex + 1].value);
    } else if (direction === 'next' && currentIndex > 0) {
      setSelectedMonth(monthOptions[currentIndex - 1].value);
    }
  };

  const handleUpdateStatus = async (item: EstablishmentBillingData, status: 'pending' | 'invoiced' | 'paid') => {
    const success = await updateBillingRecord(item.establishment.id, item.billing, { status });
    if (success) {
      toast.success(`Statut mis à jour: ${status === 'paid' ? 'Payée' : status === 'invoiced' ? 'Facturée' : 'En attente'}`);
    } else {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleUpdateNotes = async (item: EstablishmentBillingData, notes: string) => {
    if (notes === (item.record?.notes || '')) return;
    await updateBillingRecord(item.establishment.id, item.billing, { notes });
  };

  const handleUpdateInvoiceNumber = async (item: EstablishmentBillingData, invoiceNumber: string) => {
    if (invoiceNumber === (item.record?.invoice_number || '')) return;
    await updateBillingRecord(item.establishment.id, item.billing, { invoiceNumber });
  };

  const handleUpdatePlan = async (item: EstablishmentBillingData, planType: string) => {
    if (planType === item.establishment.planType) return;
    try {
      const { error } = await supabase
        .from('establishments')
        .update({ plan_type: planType })
        .eq('id', item.establishment.id);

      if (error) throw error;
      toast.success(`Plan modifié: ${BILLING_PLANS[planType]?.name || planType}`);
      // Refresh data
      window.location.reload();
    } catch (err) {
      console.error('Error updating plan:', err);
      toast.error('Erreur lors de la mise à jour du plan');
    }
  };

  const exportCSV = () => {
    const headers = ['Établissement', 'Plan', 'Réservations venues', 'Personnes', 'Base', 'Réservations', 'Total HT', 'Statut', 'N° Facture'];
    const rows = data.map(item => [
      item.establishment.name,
      item.billing.planName,
      item.stats.arrivedCount,
      item.stats.arrivedPeople,
      item.billing.baseAmount.toFixed(2),
      item.billing.reservationAmount.toFixed(2),
      item.billing.total.toFixed(2),
      item.record?.status || 'pending',
      item.record?.invoice_number || ''
    ]);

    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facturation-${selectedMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export CSV téléchargé');
  };

  if (loading) {
    return <PiktableLoader message="Chargement de la facturation..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à l'administration
          </Link>
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à l'administration
            </Link>
            <h1 className="text-2xl md:text-3xl font-serif font-bold flex items-center gap-3">
              <Receipt className="w-7 h-7 text-primary" />
              Gestion de la facturation
            </h1>
            <p className="text-muted-foreground mt-1">
              Suivi et facturation de tous les établissements
            </p>
          </div>
          <Button variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        </div>

        {/* Month Selector */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth('prev')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) >= monthOptions.length - 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Sélectionner un mois" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                size="icon"
                onClick={() => navigateMonth('next')}
                disabled={monthOptions.findIndex(m => m.value === selectedMonth) <= 0}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Établissements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {summary.activeEstablishments}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  actifs sur {summary.totalEstablishments}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4" />
                  Réservations venues
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {summary.totalArrivedReservations}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {summary.totalArrivedPeople} personnes
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Total HT à facturer
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary">
                  {formatCHF(summary.totalAmount)}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  ce mois
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  En attente
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-600">
                  {summary.pendingInvoices}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {summary.invoicedCount} facturées, {summary.paidCount} payées
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Establishments Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map(item => (
            <EstablishmentCard
              key={item.establishment.id}
              item={item}
              onUpdateStatus={(status) => handleUpdateStatus(item, status)}
              onUpdateNotes={(notes) => handleUpdateNotes(item, notes)}
              onUpdateInvoiceNumber={(number) => handleUpdateInvoiceNumber(item, number)}
              onUpdatePlan={(planType) => handleUpdatePlan(item, planType)}
            />
          ))}
        </div>

        {data.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Aucun établissement trouvé
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
